import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";

const UNASSIGNED = "__unassigned__";
const ADD_NEW = "__add_new__";

const JOB_TYPES = [
  "Leak Repair", "Drain Cleaning", "Water Heater Install", "Water Heater Repair",
  "Toilet Install", "Toilet Repair", "Faucet Install", "Faucet Repair",
  "Pipe Inspection", "Sewer Line", "Backflow Prevention", "Water Softener",
  "Water Filtration", "Gas Line", "Emergency Flood Response", "Other",
];

const PIPE_MATERIALS = ["Copper", "PEX", "PVC", "Galvanized", "Cast Iron", "Other"];

export default function NewJob() {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const presetDate = params.get("date") || "";
  const presetTime = params.get("time") || "";

  const [customers, setCustomers] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({
    customer_id: "", title: "", description: "",
    tech_id: UNASSIGNED, scheduled_date: presetDate, scheduled_time: presetTime,
    job_type: "Other", urgency: "scheduled",
    issue_description: "", pipe_material: "",
  });
  const [saving, setSaving] = useState(false);
  const [showAddCust, setShowAddCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "", address: "" });
  const [savingCust, setSavingCust] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, t] = await Promise.all([api.get("/customers"), api.get("/team")]);
      setCustomers(c.data);
      setTeam(t.data);
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.title.trim()) {
      toast.error("Customer and title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, tech_id: form.tech_id === UNASSIGNED ? null : form.tech_id };
      const { data } = await api.post("/jobs", payload);
      toast.success("Job created");
      nav(`/jobs/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const handleCustomerChange = (v) => {
    if (v === ADD_NEW) { setShowAddCust(true); return; }
    setForm({ ...form, customer_id: v });
  };

  const saveNewCustomer = async (e) => {
    e.preventDefault();
    if (!newCust.name.trim()) return;
    setSavingCust(true);
    try {
      const { data } = await api.post("/customers", newCust);
      setCustomers((arr) => [data, ...arr]);
      setForm({ ...form, customer_id: data.id });
      setShowAddCust(false);
      setNewCust({ name: "", phone: "", email: "", address: "" });
      toast.success("Customer added");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSavingCust(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <Link to="/jobs" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 font-semibold">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to jobs
      </Link>
      <h1 className="text-3xl font-bold">New Job</h1>

      <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5">
        <div>
          <Label className="text-sm font-semibold">Customer *</Label>
          <Select value={form.customer_id} onValueChange={handleCustomerChange}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="job-customer">
              <SelectValue placeholder="Choose a customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ADD_NEW} className="font-semibold text-blue-700">
                <span className="flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add New Customer</span>
              </SelectItem>
              {customers.length === 0 ? null : customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold">Job Type *</Label>
            <Select value={form.job_type} onValueChange={(v) => setForm({ ...form, job_type: v })}>
              <SelectTrigger className="h-12 mt-1.5" data-testid="job-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Urgency *</Label>
            <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
              <SelectTrigger className="h-12 mt-1.5" data-testid="job-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="emergency"><span className="text-red-600 font-bold">🚨 Emergency</span></SelectItem>
                <SelectItem value="urgent"><span className="text-amber-700 font-bold">Urgent</span></SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-sm font-semibold">Job Title *</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Water heater replacement" className="h-12 mt-1.5" data-testid="job-title" />
        </div>

        <div>
          <Label className="text-sm font-semibold">Issue Description</Label>
          <Textarea value={form.issue_description} onChange={(e) => setForm({ ...form, issue_description: e.target.value })} placeholder="What did the customer report?" className="min-h-[80px] mt-1.5" data-testid="job-issue" />
        </div>

        <div>
          <Label className="text-sm font-semibold">Internal Description</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes for the technician…" className="min-h-[80px] mt-1.5" data-testid="job-description" />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold">Pipe Material</Label>
            <Select value={form.pipe_material} onValueChange={(v) => setForm({ ...form, pipe_material: v })}>
              <SelectTrigger className="h-12 mt-1.5" data-testid="job-pipe-material"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {PIPE_MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Assign to</Label>
            <Select value={form.tech_id} onValueChange={(v) => setForm({ ...form, tech_id: v })}>
              <SelectTrigger className="h-12 mt-1.5" data-testid="job-tech"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {team.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.full_name} {m.role === "owner" ? "(Owner)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold">Scheduled Date</Label>
            <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="h-12 mt-1.5" data-testid="job-date" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Scheduled Time</Label>
            <Input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} className="h-12 mt-1.5" data-testid="job-time" />
          </div>
        </div>

        <Button type="submit" disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="job-save">
          {saving ? "Saving…" : "Create Job"}
        </Button>
      </form>

      <Dialog open={showAddCust} onOpenChange={setShowAddCust}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveNewCustomer} className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Name *</Label>
              <Input required value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} className="h-12 mt-1.5" data-testid="new-cust-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Phone</Label>
              <Input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} className="h-12 mt-1.5" data-testid="new-cust-phone" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Email</Label>
              <Input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} className="h-12 mt-1.5" data-testid="new-cust-email" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Address</Label>
              <Input value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} className="h-12 mt-1.5" data-testid="new-cust-address" />
            </div>
            <Button type="submit" disabled={savingCust} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="new-cust-save">
              {savingCust ? "Saving…" : "Save & Use Customer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
